import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, ArchiveRestore, CalendarDays, Download, Eye, FileClock, FilePlus2, FileSignature, FileUp, History, MoreHorizontal,
  Pencil, RotateCcw, ScrollText, Search, ShieldAlert, Trash2, Upload, Users, UserCheck, UserX,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Progress } from "../ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Spinner, EmptyState } from "../common";
import { StaffName } from "../StaffAvatar";
import { api, errMsg, fmtDay, fmtDateTime, downloadBlob, viewAuthedFile } from "../../lib/api";
import { invalidatePolicyAccess } from "../../lib/policies";
import { EMPTY_ASSIGNMENT, SignersPicker, useStaffDirectory } from "./SignersPicker";
import { PolicyPdfViewer } from "./PolicyPdfViewer";

/**
 * Phase 218 — the policy library shared by Admin → Policies and Super Admin → Tenant Config → Policies.
 * `rights` = {can_view, can_upload, can_edit, can_assign, can_delete, can_permanent_delete, is_super_admin}.
 */
export const AUDIT_LABELS = {
  "policy.access_changed": "Permissions changed (Super Admin)", "policy.uploaded": "Policy uploaded", "policy.version_uploaded": "New version uploaded",
  "policy.updated": "Details edited", "policy.signers_changed": "Who must sign changed", "policy.signed": "Signed by staff",
  "policy.deleted": "Deleted (moved to Deleted Policies)", "policy.restored": "Restored", "policy.permanently_deleted": "Permanently deleted (Super Admin)",
};

const todayISO = () => new Date().toISOString().slice(0, 10);

export function PoliciesManager({ code, rights, onChanged, testIdPrefix = "policies" }) {
  const [tab, setTab] = useState("active");
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [deleted, setDeleted] = useState(null);
  const [audit, setAudit] = useState(null);
  const [dlg, setDlg] = useState(null);   // {kind, policy}
  const directory = useStaffDirectory(code, !!rights?.can_view);
  const tid = (s) => `${testIdPrefix}-${s}`;

  const load = useCallback(async () => {
    if (!rights?.can_view) return;
    const params = search.trim() ? { search: search.trim() } : {};
    try {
      const [a, d, au] = await Promise.all([
        api.get(`/t/${code}/policies`, { params }), api.get(`/t/${code}/policies/deleted`, { params }),
        api.get(`/t/${code}/policies/audit`, { params: { limit: 60 } }),
      ]);
      setActive(a.data); setDeleted(d.data); setAudit(au.data.items || []);
    } catch (e) { toast.error(errMsg(e)); setActive((x) => x || { items: [], counts: {} }); setDeleted((x) => x || { items: [], counts: {} }); setAudit((x) => x || []); }
  }, [code, search, rights?.can_view]);
  useEffect(() => { const h = setTimeout(load, search ? 300 : 0); return () => clearTimeout(h); }, [load, search]);

  const changed = () => { load(); invalidatePolicyAccess(); onChanged?.(); };
  const close = () => setDlg(null);

  if (!rights?.can_view) return null;
  const counts = active?.counts || deleted?.counts || {};

  return (
    <div className="space-y-4" data-testid={tid("manager")}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-full sm:w-80">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search policies..." className="pl-8 h-9" data-testid={tid("search")} />
        </div>
        <div className="flex items-center gap-2">
          {rights.can_upload && (
            <Button onClick={() => setDlg({ kind: "upload" })} data-testid={tid("upload-button")}><Upload className="h-4 w-4 mr-1.5" /> Upload policy</Button>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="active" data-testid={tid("tab-active")}><ScrollText className="h-3.5 w-3.5 mr-1.5" /> Policies {counts.active > 0 && <Badge variant="secondary" className="ml-1.5 text-[10px]">{counts.active}</Badge>}</TabsTrigger>
          <TabsTrigger value="deleted" data-testid={tid("tab-deleted")}><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Deleted Policies {counts.deleted > 0 && <Badge variant="secondary" className="ml-1.5 text-[10px]">{counts.deleted}</Badge>}</TabsTrigger>
          <TabsTrigger value="activity" data-testid={tid("tab-activity")}><History className="h-3.5 w-3.5 mr-1.5" /> Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-4">
          {!active ? <Spinner /> : active.items.length === 0 ? (
            <Card className="shadow-sm"><CardContent className="p-0">
              <EmptyState title={search ? "No policies match your search" : "No policies yet"}
                subtitle={rights.can_upload ? "Upload a PDF with a title, a short description and an effective date, then choose who must sign it." : "Policies uploaded for this municipality will appear here."}
                action={rights.can_upload && !search ? <Button onClick={() => setDlg({ kind: "upload" })} data-testid={tid("upload-empty-button")}><Upload className="h-4 w-4 mr-1.5" /> Upload the first policy</Button> : null} />
            </CardContent></Card>
          ) : (
            <Card className="shadow-sm"><CardContent className="p-0 overflow-x-auto">
              <Table data-testid={tid("table")}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Policy</TableHead>
                    <TableHead>Effective</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Who must sign</TableHead>
                    <TableHead className="min-w-[180px]">Sign-off</TableHead>
                    <TableHead>Last updated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {active.items.map((p) => (
                    <TableRow key={p.id} data-testid={tid(`row-${p.id}`)}>
                      <TableCell className="max-w-[320px]">
                        <button type="button" className="text-left font-medium hover:underline" onClick={() => setDlg({ kind: "view", policy: p })} data-testid={tid(`title-${p.id}`)}>{p.title}</button>
                        {p.description && <p className="text-xs text-muted-foreground line-clamp-2" data-testid={tid(`desc-${p.id}`)}>{p.description}</p>}
                        <p className="text-[11px] text-muted-foreground font-mono truncate">{p.filename}</p>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm" data-testid={tid(`effective-${p.id}`)}>{fmtDay(p.effective_date)}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        <Badge variant="outline" className="font-mono" data-testid={tid(`version-${p.id}`)}>v{p.version}</Badge>
                        {p.version_count > 1 && (
                          <button type="button" className="block text-[11px] text-primary hover:underline mt-1" onClick={() => setDlg({ kind: "history", policy: p })} data-testid={tid(`history-${p.id}`)}>
                            {p.version_count - 1} earlier version{p.version_count - 1 === 1 ? "" : "s"}
                          </button>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[220px]">
                        <p className={`text-sm ${p.assignment_summary?.empty ? "text-amber-700" : ""}`} data-testid={tid(`signers-${p.id}`)}>{p.assignment_summary?.text}</p>
                        <p className="text-[11px] text-muted-foreground">{p.counts?.required} staff</p>
                        {rights.can_assign && (
                          <button type="button" className="text-[11px] text-primary hover:underline" onClick={() => setDlg({ kind: "signers", policy: p })} data-testid={tid(`edit-signers-${p.id}`)}>Change</button>
                        )}
                      </TableCell>
                      <TableCell>
                        <button type="button" className="w-full text-left group" onClick={() => setDlg({ kind: "signoff", policy: p })} data-testid={tid(`signoff-${p.id}`)}>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="tabular-nums font-medium">{p.counts?.signed} of {p.counts?.required} signed</span>
                            <span className={`tabular-nums ${p.counts?.pending ? "text-amber-700" : "text-emerald-700"}`}>{p.counts?.pending ? `${p.counts.pending} pending` : p.counts?.required ? "Complete" : "—"}</span>
                          </div>
                          <Progress value={p.counts?.pct ?? 0} className="h-2" />
                          <span className="text-[11px] text-primary group-hover:underline">Sign-off report</span>
                        </button>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        <p>{fmtDateTime(p.updated_at)}</p>
                        <p>by {p.updated_by_name || p.uploaded_by_name}</p>
                      </TableCell>
                      <TableCell className="text-right">
                        <RowActions p={p} rights={rights} tid={tid} onAction={(kind) => setDlg({ kind, policy: p })} code={code} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="deleted" className="mt-4">
          {!deleted ? <Spinner /> : deleted.items.length === 0 ? (
            <Card className="shadow-sm"><CardContent className="p-0"><EmptyState title="No deleted policies" subtitle="Deleted policies stay here until they are restored or permanently deleted by the Super Admin." /></CardContent></Card>
          ) : (
            <Card className="shadow-sm"><CardContent className="p-0 overflow-x-auto">
              <Table data-testid={tid("deleted-table")}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Policy</TableHead><TableHead>Version</TableHead><TableHead>Deleted</TableHead><TableHead>Signatures on record</TableHead><TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deleted.items.map((p) => (
                    <TableRow key={p.id} data-testid={tid(`deleted-row-${p.id}`)}>
                      <TableCell className="max-w-[320px]">
                        <p className="font-medium">{p.title}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">{p.description}</p>
                        <p className="text-[11px] text-muted-foreground">Effective {fmtDay(p.effective_date)}</p>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="font-mono">v{p.version}</Badge></TableCell>
                      <TableCell className="text-xs">
                        <p data-testid={tid(`deleted-at-${p.id}`)}>{fmtDateTime(p.deleted_at)}</p>
                        <p className="text-muted-foreground">by {p.deleted_by_name}{p.deleted_reason ? ` — ${p.deleted_reason}` : ""}</p>
                      </TableCell>
                      <TableCell className="text-sm tabular-nums">{p.counts?.signed_total ?? 0} on current version</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button variant="ghost" size="sm" className="h-8" onClick={() => setDlg({ kind: "view", policy: p })} data-testid={tid(`deleted-view-${p.id}`)}><Eye className="h-3.5 w-3.5 mr-1" /> View</Button>
                          {rights.can_delete && (
                            <Button variant="outline" size="sm" className="h-8" onClick={() => setDlg({ kind: "restore", policy: p })} data-testid={tid(`restore-${p.id}`)}><ArchiveRestore className="h-3.5 w-3.5 mr-1" /> Restore</Button>
                          )}
                          {rights.can_permanent_delete && (
                            <Button variant="destructive" size="sm" className="h-8" onClick={() => setDlg({ kind: "purge", policy: p })} data-testid={tid(`purge-${p.id}`)}><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete permanently</Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          {!audit ? <Spinner /> : audit.length === 0 ? (
            <Card className="shadow-sm"><CardContent className="p-0"><EmptyState title="No policy activity yet" subtitle="Uploads, edits, signer changes, signatures, deletions and permission changes are listed here with date, time and user." /></CardContent></Card>
          ) : (
            <Card className="shadow-sm"><CardContent className="p-0 overflow-x-auto">
              <Table data-testid={tid("activity-table")}>
                <TableHeader><TableRow><TableHead>When (IST)</TableHead><TableHead>Who</TableHead><TableHead>Action</TableHead><TableHead>Details</TableHead></TableRow></TableHeader>
                <TableBody>
                  {audit.map((a) => (
                    <TableRow key={a.id} data-testid={tid(`activity-${a.id}`)}>
                      <TableCell className="whitespace-nowrap text-xs">{fmtDateTime(a.created_at)}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{a.user_name}<span className="text-muted-foreground"> • {a.actor?.role_label || a.user_role}</span></TableCell>
                      <TableCell className="text-xs whitespace-nowrap"><Badge variant="outline" className="text-[10px]">{AUDIT_LABELS[a.action] || a.action}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[520px]">{a.summary}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      {dlg?.kind === "upload" && <UploadDialog code={code} rights={rights} directory={directory} onClose={close} onDone={() => { close(); changed(); }} tid={tid} />}
      {dlg?.kind === "edit" && <EditDialog code={code} policy={dlg.policy} onClose={close} onDone={() => { close(); changed(); }} tid={tid} />}
      {dlg?.kind === "version" && <VersionDialog code={code} policy={dlg.policy} onClose={close} onDone={() => { close(); changed(); }} tid={tid} />}
      {dlg?.kind === "signers" && <SignersDialog code={code} policy={dlg.policy} directory={directory} onClose={close} onDone={() => { close(); changed(); }} tid={tid} />}
      {dlg?.kind === "signoff" && <SignoffDialog code={code} policy={dlg.policy} onClose={close} tid={tid} />}
      {dlg?.kind === "history" && <HistoryDialog code={code} policy={dlg.policy} onClose={close} tid={tid} />}
      {dlg?.kind === "view" && <ViewDialog code={code} policy={dlg.policy} onClose={close} tid={tid} />}
      {dlg?.kind === "delete" && <DeleteDialog code={code} policy={dlg.policy} onClose={close} onDone={() => { close(); changed(); }} tid={tid} />}
      {dlg?.kind === "restore" && <RestoreDialog code={code} policy={dlg.policy} onClose={close} onDone={() => { close(); changed(); }} tid={tid} />}
      {dlg?.kind === "purge" && <PurgeDialog code={code} policy={dlg.policy} onClose={close} onDone={() => { close(); changed(); }} tid={tid} />}
    </div>
  );
}

function RowActions({ p, rights, tid, onAction }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Actions for ${p.title}`} data-testid={tid(`actions-${p.id}`)}><MoreHorizontal className="h-4 w-4" /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={() => onAction("view")} data-testid={tid(`action-view-${p.id}`)}><Eye className="h-4 w-4 mr-2" /> View PDF</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onAction("signoff")} data-testid={tid(`action-signoff-${p.id}`)}><FileSignature className="h-4 w-4 mr-2" /> Sign-off report</DropdownMenuItem>
        {p.version_count > 1 && <DropdownMenuItem onClick={() => onAction("history")} data-testid={tid(`action-history-${p.id}`)}><FileClock className="h-4 w-4 mr-2" /> Version history</DropdownMenuItem>}
        {(rights.can_edit || rights.can_upload || rights.can_assign) && <DropdownMenuSeparator />}
        {rights.can_edit && <DropdownMenuItem onClick={() => onAction("edit")} data-testid={tid(`action-edit-${p.id}`)}><Pencil className="h-4 w-4 mr-2" /> Edit details</DropdownMenuItem>}
        {rights.can_upload && <DropdownMenuItem onClick={() => onAction("version")} data-testid={tid(`action-version-${p.id}`)}><FileUp className="h-4 w-4 mr-2" /> Upload new version</DropdownMenuItem>}
        {rights.can_assign && <DropdownMenuItem onClick={() => onAction("signers")} data-testid={tid(`action-signers-${p.id}`)}><Users className="h-4 w-4 mr-2" /> Who must sign</DropdownMenuItem>}
        {rights.can_delete && (<><DropdownMenuSeparator /><DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => onAction("delete")} data-testid={tid(`action-delete-${p.id}`)}><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem></>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const ResignNotice = ({ testId }) => (
  <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid={testId}>
    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
    <span>Saving this means <b>every selected staff member must read and sign the policy again</b> at their next login. Earlier signatures stay in the records.</span>
  </div>
);

function PdfInput({ file, setFile, tid, id = "policy-file" }) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>Policy document (PDF)</Label>
      <Input id={id} type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="h-10 file:mr-3 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:px-3 file:py-1.5 file:text-xs" data-testid={tid("file-input")} />
      {file && <p className="text-[11px] text-muted-foreground" data-testid={tid("file-name")}>{file.name} • {(file.size / 1024).toFixed(0)} KB</p>}
      <p className="text-[11px] text-muted-foreground">PDF only, up to 15 MB.</p>
    </div>
  );
}

function UploadDialog({ code, rights, directory, onClose, onDone, tid }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [effective, setEffective] = useState(todayISO());
  const [file, setFile] = useState(null);
  const [assignment, setAssignment] = useState({ ...EMPTY_ASSIGNMENT });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose the PDF document first");
    if (!file.name.toLowerCase().endsWith(".pdf") && file.type !== "application/pdf") return toast.error("Only PDF documents are accepted");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("title", title.trim()); fd.append("description", description.trim()); fd.append("effective_date", effective);
      if (rights.can_assign) fd.append("assignment", JSON.stringify(assignment));
      const r = await api.post(`/t/${code}/policies`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`"${r.data.title}" uploaded${r.data.counts?.required ? ` — ${r.data.counts.required} staff must sign at their next login` : ""}`);
      onDone();
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid={tid("upload-dialog")}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><FilePlus2 className="h-5 w-5" /> Upload a policy</DialogTitle>
          <DialogDescription>Upload the PDF with a title, a short description and the date it comes into effect.{rights.can_assign ? " Choose who must sign it below — they will be asked at their next login." : " The Super Admin or an admin with Assign permission chooses who must sign."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-[1fr_180px] gap-3">
            <div className="space-y-1">
              <Label htmlFor="policy-title">Title</Label>
              <Input id="policy-title" value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={160} placeholder="e.g. Code of Conduct" data-testid={tid("title-input")} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="policy-effective">Effective date</Label>
              <Input id="policy-effective" type="date" value={effective} onChange={(e) => setEffective(e.target.value)} required data-testid={tid("effective-input")} />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="policy-description">Short description</Label>
            <Textarea id="policy-description" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1000} rows={3} placeholder="What this policy covers, in one or two sentences" data-testid={tid("description-input")} />
          </div>
          <PdfInput file={file} setFile={setFile} tid={tid} />
          {rights.can_assign && (
            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-1.5"><Users className="h-4 w-4" /> Who must sign</p>
              <SignersPicker code={code} value={assignment} onChange={setAssignment} directory={directory} testIdPrefix={`${tid("upload")}-signers`} />
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid={tid("upload-cancel")}>Cancel</Button>
            <Button type="submit" disabled={busy || !title.trim() || !effective || !file} data-testid={tid("upload-submit")}><Upload className="h-4 w-4 mr-1.5" /> {busy ? "Uploading..." : "Upload policy"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ code, policy, onClose, onDone, tid }) {
  const [title, setTitle] = useState(policy.title || "");
  const [description, setDescription] = useState(policy.description || "");
  const [effective, setEffective] = useState(policy.effective_date || "");
  const [busy, setBusy] = useState(false);
  const dirty = title.trim() !== policy.title || description.trim() !== (policy.description || "") || effective !== policy.effective_date;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.put(`/t/${code}/policies/${policy.id}`, { title: title.trim(), description: description.trim(), effective_date: effective });
      toast.success(r.data.resign_required ? `"${r.data.title}" updated — everyone selected must sign again` : "No changes to save");
      onDone();
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid={tid("edit-dialog")}>
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Pencil className="h-5 w-5" /> Edit policy details</DialogTitle><DialogDescription>Title, short description and effective date of "{policy.title}" (v{policy.version}).</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1"><Label htmlFor="edit-title">Title</Label><Input id="edit-title" value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={160} data-testid={tid("edit-title")} /></div>
          <div className="space-y-1"><Label htmlFor="edit-desc">Short description</Label><Textarea id="edit-desc" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1000} rows={3} data-testid={tid("edit-description")} /></div>
          <div className="space-y-1"><Label htmlFor="edit-eff">Effective date</Label><Input id="edit-eff" type="date" value={effective} onChange={(e) => setEffective(e.target.value)} required className="w-48" data-testid={tid("edit-effective")} /></div>
          {dirty && <ResignNotice testId={tid("edit-resign-notice")} />}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy || !dirty || !title.trim() || !effective} data-testid={tid("edit-submit")}>{busy ? "Saving..." : "Save changes"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function VersionDialog({ code, policy, onClose, onDone, tid }) {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [effective, setEffective] = useState(policy.effective_date || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose the new PDF first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); if (note.trim()) fd.append("note", note.trim()); if (effective && effective !== policy.effective_date) fd.append("effective_date", effective);
      const r = await api.post(`/t/${code}/policies/${policy.id}/version`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`"${r.data.title}" is now v${r.data.version} — everyone selected must sign again`);
      onDone();
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid={tid("version-dialog")}>
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><FileUp className="h-5 w-5" /> Upload a new version</DialogTitle><DialogDescription>"{policy.title}" is currently v{policy.version}. The new file becomes v{policy.version + 1}; v{policy.version} is kept in the version history.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <PdfInput file={file} setFile={setFile} tid={(s) => tid(`version-${s}`)} id="version-file" />
          <div className="space-y-1"><Label htmlFor="version-note">What changed (optional)</Label><Input id="version-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="e.g. Annual review — leave rules updated" data-testid={tid("version-note")} /></div>
          <div className="space-y-1"><Label htmlFor="version-eff">Effective date</Label><Input id="version-eff" type="date" value={effective} onChange={(e) => setEffective(e.target.value)} className="w-48" data-testid={tid("version-effective")} /></div>
          <ResignNotice testId={tid("version-resign-notice")} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy || !file} data-testid={tid("version-submit")}>{busy ? "Uploading..." : `Upload v${policy.version + 1}`}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SignersDialog({ code, policy, directory, onClose, onDone, tid }) {
  const [value, setValue] = useState({ ...EMPTY_ASSIGNMENT, ...(policy.assignment || {}) });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/t/${code}/policies/${policy.id}/signers`, value);
      const a = r.data.added?.length || 0, rm = r.data.removed?.length || 0;
      toast.success(`Who must sign saved — ${r.data.required_count} staff${a ? `, ${a} newly required` : ""}${rm ? `, ${rm} no longer required` : ""}`);
      onDone();
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid={tid("signers-dialog")}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Users className="h-5 w-5" /> Who must sign "{policy.title}"</DialogTitle>
          <DialogDescription>Newly added staff are asked to sign at their next login. Staff you unselect no longer see the policy; their earlier signatures stay in the records.</DialogDescription>
        </DialogHeader>
        <SignersPicker code={code} value={value} onChange={setValue} directory={directory} testIdPrefix={tid("signers")} />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="button" onClick={save} disabled={busy} data-testid={tid("signers-save")}>{busy ? "Saving..." : "Save who must sign"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, sub, icon: Icon, tone = "", testId }) {
  return (
    <div className="rounded-lg border px-3 py-2.5 bg-card" data-testid={testId}>
      <p className="text-[11px] text-muted-foreground flex items-center gap-1">{Icon && <Icon className="h-3.5 w-3.5" />}{label}</p>
      <p className={`text-xl font-semibold tabular-nums ${tone}`}>{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

export function SignoffDialog({ code, policy, onClose, tid }) {
  const [rep, setRep] = useState(null);
  const [tab, setTab] = useState("pending");
  useEffect(() => { api.get(`/t/${code}/policies/${policy.id}/signoff`).then((r) => { setRep(r.data); setTab(r.data.summary.pending > 0 ? "pending" : "signed"); }).catch((e) => { toast.error(errMsg(e)); onClose(); }); }, [code, policy.id, onClose]);
  const exportXlsx = () => downloadBlob(`/t/${code}/policies/${policy.id}/signoff/export`, `policy_signoff_${policy.id}.xlsx`).then(() => toast.success("Excel downloaded")).catch((e) => toast.error(errMsg(e)));
  const s = rep?.summary;
  const Person = ({ r }) => (
    <StaffName code={code} userId={r.user_id} name={r.name} empCode={r.employee_id} size="xs" sub={<>{r.custom_role_name || r.role_label}{r.designation ? ` • ${r.designation}` : ""}{r.is_active === false ? " • inactive" : ""}</>} />
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid={tid("signoff-dialog")}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><FileSignature className="h-5 w-5" /> Policy Sign-off — {policy.title}</DialogTitle>
          <DialogDescription>Who has signed the current version (v{policy.version}), who is still pending, and every earlier signature on record. {rep?.assignment_summary?.text ? `Who must sign: ${rep.assignment_summary.text}.` : ""}</DialogDescription>
        </DialogHeader>
        {!rep ? <Spinner /> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Required to sign" value={s.required} icon={Users} testId={tid("signoff-required")} />
              <Stat label="Signed" value={s.signed} icon={UserCheck} tone="text-emerald-700" sub={s.signed_not_required ? `+${s.signed_not_required} no longer selected` : null} testId={tid("signoff-signed")} />
              <Stat label="Pending" value={s.pending} icon={UserX} tone={s.pending ? "text-amber-700" : ""} testId={tid("signoff-pending")} />
              <Stat label="Completion" value={s.pct === null ? "—" : `${s.pct}%`} icon={ShieldAlert} sub={s.revision_changed_at ? `since ${fmtDateTime(s.revision_changed_at)}` : null} testId={tid("signoff-pct")} />
            </div>
            <div className="flex items-center justify-between gap-2">
              <Tabs value={tab} onValueChange={setTab} className="flex-1">
                <TabsList>
                  <TabsTrigger value="pending" data-testid={tid("signoff-tab-pending")}>Pending ({rep.pending.length})</TabsTrigger>
                  <TabsTrigger value="signed" data-testid={tid("signoff-tab-signed")}>Signed ({rep.signed.length})</TabsTrigger>
                  <TabsTrigger value="earlier" data-testid={tid("signoff-tab-earlier")}>Earlier versions ({rep.earlier.length})</TabsTrigger>
                </TabsList>
              </Tabs>
              <Button variant="outline" size="sm" onClick={exportXlsx} data-testid={tid("signoff-export")}><Download className="h-4 w-4 mr-1.5" /> Export Excel</Button>
            </div>
            {tab === "pending" && (rep.pending.length === 0 ? <EmptyState title="Nobody pending" subtitle={s.required ? "Everyone selected has signed the current version." : "Nobody is selected to sign this policy yet."} /> : (
              <Table data-testid={tid("signoff-pending-table")}>
                <TableHeader><TableRow><TableHead>Staff member</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>{rep.pending.map((r) => (<TableRow key={r.user_id} data-testid={tid(`signoff-pending-${r.user_id}`)}><TableCell><Person r={r} /></TableCell><TableCell><Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">Pending — signs at next login</Badge></TableCell></TableRow>))}</TableBody>
              </Table>
            ))}
            {tab === "signed" && (rep.signed.length === 0 ? <EmptyState title="No signatures yet on this version" /> : (
              <Table data-testid={tid("signoff-signed-table")}>
                <TableHeader><TableRow><TableHead>Staff member</TableHead><TableHead>Signed on (IST)</TableHead><TableHead>Typed signature</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>{rep.signed.map((r) => (
                  <TableRow key={r.signature_id} data-testid={tid(`signoff-signed-${r.user_id}`)}>
                    <TableCell><Person r={r} /></TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{fmtDateTime(r.signed_at)}</TableCell>
                    <TableCell className="font-heading text-sm italic">{r.typed_name}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {r.still_required ? <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">Signed</Badge> : <Badge variant="outline">Signed — no longer selected</Badge>}
                        {r.read_to_end && <Badge variant="outline" className="text-[10px]" title={r.pages_read ? `Scrolled through all ${r.pages_read} page(s) before signing` : "Scrolled to the last page before signing"} data-testid={tid(`signoff-read-${r.user_id}`)}>Read to last page</Badge>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            ))}
            {tab === "earlier" && (rep.earlier.length === 0 ? <EmptyState title="No earlier-version signatures" subtitle="Signatures on previous versions or details stay here for the record." /> : (
              <Table data-testid={tid("signoff-earlier-table")}>
                <TableHeader><TableRow><TableHead>Staff member</TableHead><TableHead>Signed on (IST)</TableHead><TableHead>Version</TableHead><TableHead>Typed signature</TableHead></TableRow></TableHeader>
                <TableBody>{rep.earlier.map((r) => (
                  <TableRow key={r.signature_id}><TableCell><Person r={r} /></TableCell><TableCell className="whitespace-nowrap text-sm">{fmtDateTime(r.signed_at)}</TableCell><TableCell><Badge variant="outline" className="font-mono">v{r.version}</Badge><span className="text-[11px] text-muted-foreground ml-1">rev {r.revision}</span></TableCell><TableCell className="font-heading text-sm italic">{r.typed_name}</TableCell></TableRow>
                ))}</TableBody>
              </Table>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ code, policy, onClose, tid }) {
  const versions = [...(policy.versions || [])].sort((a, b) => (b.version || 0) - (a.version || 0));
  const open = (v) => viewAuthedFile(`/t/${code}/policies/${policy.id}/file?version=${v}`, `${policy.title}-v${v}.pdf`).catch((e) => toast.error(errMsg(e)));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid={tid("history-dialog")}>
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><FileClock className="h-5 w-5" /> Version history — {policy.title}</DialogTitle><DialogDescription>Every earlier document is kept. Only the current version (v{policy.version}) is shown to staff and used for signing.</DialogDescription></DialogHeader>
        <Table data-testid={tid("history-table")}>
          <TableHeader><TableRow><TableHead>Version</TableHead><TableHead>Document</TableHead><TableHead>Effective</TableHead><TableHead>Uploaded</TableHead><TableHead className="text-right">Open</TableHead></TableRow></TableHeader>
          <TableBody>
            <TableRow className="bg-primary/5" data-testid={tid("history-current")}>
              <TableCell><Badge className="font-mono">v{policy.version}</Badge><span className="text-[11px] text-muted-foreground ml-1">current</span></TableCell>
              <TableCell className="text-xs font-mono">{policy.filename}{policy.version_note ? <p className="font-sans text-muted-foreground">{policy.version_note}</p> : null}</TableCell>
              <TableCell className="text-sm whitespace-nowrap">{fmtDay(policy.effective_date)}</TableCell>
              <TableCell className="text-xs whitespace-nowrap">{fmtDateTime(policy.uploaded_at)}<p className="text-muted-foreground">by {policy.uploaded_by_name}</p></TableCell>
              <TableCell className="text-right"><Button variant="ghost" size="sm" className="h-8" onClick={() => open(policy.version)}><Eye className="h-3.5 w-3.5 mr-1" /> Open</Button></TableCell>
            </TableRow>
            {versions.map((v) => (
              <TableRow key={`${v.version}-${v.file_id}`} data-testid={tid(`history-v${v.version}`)}>
                <TableCell><Badge variant="outline" className="font-mono">v{v.version}</Badge></TableCell>
                <TableCell className="text-xs font-mono">{v.filename}{v.title !== policy.title ? <p className="font-sans text-muted-foreground">Title then: {v.title}</p> : null}</TableCell>
                <TableCell className="text-sm whitespace-nowrap">{fmtDay(v.effective_date)}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{fmtDateTime(v.uploaded_at)}<p className="text-muted-foreground">by {v.uploaded_by_name}</p><p className="text-muted-foreground">replaced {fmtDateTime(v.superseded_at)}</p></TableCell>
                <TableCell className="text-right"><Button variant="ghost" size="sm" className="h-8" onClick={() => open(v.version)} data-testid={tid(`history-open-v${v.version}`)}><Eye className="h-3.5 w-3.5 mr-1" /> Open</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
}

function ViewDialog({ code, policy, onClose, tid }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid={tid("view-dialog")}>
        <DialogHeader>
          <DialogTitle className="font-heading">{policy.title} <Badge variant="outline" className="font-mono ml-1">v{policy.version}</Badge>{policy.status === "deleted" && <Badge variant="destructive" className="ml-1">Deleted</Badge>}</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-x-3"><span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> Effective {fmtDay(policy.effective_date)}</span>{policy.description && <span>{policy.description}</span>}</DialogDescription>
        </DialogHeader>
        <PolicyPdfViewer code={code} policy={policy} height="h-[65vh] min-h-[360px]" testId={tid("view-pdf")} />
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ code, policy, onClose, onDone, tid }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.delete(`/t/${code}/policies/${policy.id}`, { params: reason.trim() ? { reason: reason.trim() } : {} }); toast.success(`"${policy.title}" moved to Deleted Policies`); onDone(); }
    catch (ex) { toast.error(errMsg(ex)); setBusy(false); }
  };
  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid={tid("delete-dialog")}>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete "{policy.title}"?</AlertDialogTitle>
          <AlertDialogDescription>It moves to the Deleted Policies tab and can be restored any time. Staff stop seeing it and are no longer asked to sign; every signature stays in the records. Only the Super Admin can delete it permanently.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-1"><Label htmlFor="delete-reason">Reason (optional)</Label><Input id="delete-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} placeholder="e.g. Superseded by the new HR handbook" data-testid={tid("delete-reason")} /></div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={tid("delete-cancel")}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); go(); }} disabled={busy} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={tid("delete-confirm")}>{busy ? "Deleting..." : "Move to Deleted Policies"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function RestoreDialog({ code, policy, onClose, onDone, tid }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post(`/t/${code}/policies/${policy.id}/restore`); toast.success(`"${policy.title}" restored`); onDone(); }
    catch (ex) { toast.error(errMsg(ex)); setBusy(false); }
  };
  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid={tid("restore-dialog")}>
        <AlertDialogHeader><AlertDialogTitle>Restore "{policy.title}"?</AlertDialogTitle><AlertDialogDescription>The policy becomes active again exactly as it was (v{policy.version}, same signers). Anyone selected who has not signed the current version is asked at their next login.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); go(); }} disabled={busy} data-testid={tid("restore-confirm")}><RotateCcw className="h-4 w-4 mr-1.5" /> {busy ? "Restoring..." : "Restore"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function PurgeDialog({ code, policy, onClose, onDone, tid }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { const r = await api.delete(`/t/${code}/policies/${policy.id}/permanent`, { params: { confirm: "DELETE" } }); toast.success(`"${policy.title}" permanently deleted (${r.data.signature_count} signature records kept)`); onDone(); }
    catch (ex) { toast.error(errMsg(ex)); setBusy(false); }
  };
  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid={tid("purge-dialog")}>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive"><ShieldAlert className="h-5 w-5" /> Permanently delete "{policy.title}"?</AlertDialogTitle>
          <AlertDialogDescription>This cannot be undone. All document versions are removed. Signature records are kept for the audit trail. Type <b>DELETE</b> to confirm.</AlertDialogDescription>
        </AlertDialogHeader>
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="DELETE" className="font-mono" data-testid={tid("purge-input")} />
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); go(); }} disabled={busy || text !== "DELETE"} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={tid("purge-confirm")}>{busy ? "Deleting..." : "Delete permanently"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export const useMemoRights = (data) => useMemo(() => data || {}, [data]);
